import AuthService, { LoginResponse, UserInfo } from "./AuthService";

/**
 * @namespace com.ang.portal.service
 */

export type UserRecord = UserInfo & {
	/** Stated by the list endpoint. `roles` holds only directly assigned roles, so it
	 * cannot be used to tell whether an account is a system admin. */
	is_system_admin?: boolean;
	/** Whether this is the system admin seeded at startup. */
	is_root_admin?: boolean;
	/** Whether the user has an authenticator set up. */
	two_factor_enabled?: boolean;
};

export interface UserLookupResponse {
	roles: string[];
	permissions: string[];
	groups: string[];
}

export interface UserLookupItem {
	key: string;
	text: string;
}

export interface CreateUserPayload {
	email: string;
	password: string;
	name?: string;
	company_name?: string;
	roles: string[];
	permissions: string[];
	groups: string[];
}

export interface UpdateUserPayload {
	email: string;
	password?: string;
	name?: string;
	company_name?: string;
	roles: string[];
	permissions: string[];
	groups: string[];
}

export interface UpdateUserResponse {
	user: UserRecord;
	accessToken?: LoginResponse["accessToken"];
	refreshToken?: LoginResponse["refreshToken"];
}

async function parseError(response: Response, fallbackMessage: string): Promise<Error> {
	const error = await response.json().catch(() => ({ message: fallbackMessage })) as { message?: string };
	return new Error(error.message || fallbackMessage);
}

const UsersService = {
	async getUsers(): Promise<UserRecord[]> {
		const response = await AuthService.authenticatedFetch("/api/users");
		if (!response.ok) {
			throw await parseError(response, "Failed to fetch users");
		}
		return response.json() as Promise<UserRecord[]>;
	},

	async getLookups(): Promise<UserLookupResponse> {
		const response = await AuthService.authenticatedFetch("/api/users/lookups");
		if (!response.ok) {
			throw await parseError(response, "Failed to fetch lookup data");
		}
		return response.json() as Promise<UserLookupResponse>;
	},

	async createUser(payload: CreateUserPayload): Promise<UserRecord> {
		const response = await AuthService.authenticatedFetch("/api/users", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(payload)
		});
		if (!response.ok) {
			throw await parseError(response, "Failed to create user");
		}
		return response.json() as Promise<UserRecord>;
	},

	async updateUser(userId: number, payload: UpdateUserPayload): Promise<UpdateUserResponse> {
		const response = await AuthService.authenticatedFetch(`/api/users/${userId}`, {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(payload)
		});
		if (!response.ok) {
			throw await parseError(response, "Failed to update user");
		}
		return response.json() as Promise<UpdateUserResponse>;
	},

	async deleteUser(userId: number): Promise<void> {
		const response = await AuthService.authenticatedFetch(`/api/users/${userId}`, {
			method: "DELETE"
		});
		if (!response.ok) {
			throw await parseError(response, "Failed to delete user");
		}
	},

	async toggleActive(userId: number): Promise<{ id: number; is_active: boolean }> {
		const response = await AuthService.authenticatedFetch(`/api/users/${userId}/toggle-active`, {
			method: "PATCH"
		});
		if (!response.ok) {
			throw await parseError(response, "Failed to toggle user status");
		}
		return response.json() as Promise<{ id: number; is_active: boolean }>;
	},

	/** Clear a user's 2FA (lost authenticator) and sign them out. */
	async reset2FA(userId: number): Promise<void> {
		const response = await AuthService.authenticatedFetch(`/api/users/${userId}/reset-2fa`, {
			method: "POST"
		});
		if (!response.ok) {
			throw await parseError(response, "Failed to reset two-factor authentication");
		}
	}
};

export default UsersService;